import { motion } from 'framer-motion';
import {
  Shield,
  TrendingUp,
  AlertTriangle,
  FileText,
  Tags,
  Sparkles,
  Eye,
  Edit3,
  Trash2,
  Plus,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, CurrentUser, blogApi, BlogPost, BlogTag, BlogCategory } from '../services/api';
import Header from '../components/Header';
import BlogPostEditor from '../components/BlogPostEditor';

// Using real API calls now

// Using imported types from api.ts

export default function BlogAdminPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<
    'posts' | 'tags' | 'ai-generator' | 'analytics'
  >('posts');

  // Blog data state
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [_isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);

  // Check if user is authorized
  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        const response = await authApi.getCurrentUser();
        if (response.success && response.data) {
          setCurrentUser(response.data);

          // Redirect if not a super user
          if (!response.data.superUser) {
            navigate('/dashboard');
            return;
          }
        } else {
          navigate('/dashboard');
          return;
        }
      } catch (err) {
        setError('Failed to verify authorization');
        navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthorization();
  }, [navigate]);

  // Load blog data
  useEffect(() => {
    if (currentUser?.superUser) {
      loadBlogData();
    }
  }, [currentUser]);

  const loadBlogData = async () => {
    try {
      setIsLoadingPosts(true);
      setIsLoadingTags(true);
      setIsLoadingCategories(true);

      const [postsResponse, tagsResponse, categoriesResponse] = await Promise.all([
        blogApi.admin.getPosts(),
        blogApi.tags.getTags(),
        blogApi.categories.getCategories(),
      ]);

      if (postsResponse.success && postsResponse.data) {
        setPosts(postsResponse.data.posts);
      }

      if (tagsResponse.success && tagsResponse.data) {
        setTags(tagsResponse.data);
      }

      if (categoriesResponse.success && categoriesResponse.data) {
        setCategories(categoriesResponse.data);
      }
    } catch (err) {
      console.error('Error loading blog data:', err);
      setError('Failed to load blog data');
    } finally {
      setIsLoadingPosts(false);
      setIsLoadingTags(false);
      setIsLoadingCategories(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const handleCreatePost = () => {
    setEditingPost(null);
    setShowEditor(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setShowEditor(true);
  };

  const handleDeletePost = async (post: BlogPost) => {
    if (window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
      try {
        const response = await blogApi.admin.deletePost(post.id);
        if (response.success) {
          setPosts(posts.filter((p) => p.id !== post.id));
        }
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleSavePost = (savedPost: BlogPost) => {
    if (editingPost) {
      // Update existing post
      setPosts(posts.map((p) => (p.id === savedPost.id ? savedPost : p)));
    } else {
      // Add new post
      setPosts([savedPost, ...posts]);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="h-screen bg-gray-50 overflow-hidden flex flex-col">
      {/* Header */}
      <Header
        title="Blog Administration"
        showMetrics={false}
        actions={
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCreatePost}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>New Post</span>
            </button>
            <div className="flex items-center space-x-2 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
              <Shield className="h-4 w-4" />
              <span>Super User</span>
            </div>
          </div>
        }
      />

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-80 bg-white border-r border-gray-200 overflow-y-auto scrollbar-hide p-6 flex-shrink-0">
          <div className="space-y-6">
            {/* Blog Info */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Blog Management</h2>
              <p className="text-sm text-gray-600">Create and manage blog content</p>
            </div>

            {/* Blog Navigation */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">Blog Functions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setActiveSection('posts')}
                  className={`w-full flex items-center space-x-3 p-3 text-left border rounded-lg transition-colors ${
                    activeSection === 'posts'
                      ? 'bg-blue-50 border-blue-200'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">Posts</div>
                    <div className="text-xs text-gray-500">{posts.length} total posts</div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveSection('tags')}
                  className={`w-full flex items-center space-x-3 p-3 text-left border rounded-lg transition-colors ${
                    activeSection === 'tags'
                      ? 'bg-green-50 border-green-200'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Tags className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="font-medium text-gray-900">Tags</div>
                    <div className="text-xs text-gray-500">{tags.length} tags available</div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveSection('ai-generator')}
                  className={`w-full flex items-center space-x-3 p-3 text-left border rounded-lg transition-colors ${
                    activeSection === 'ai-generator'
                      ? 'bg-purple-50 border-purple-200'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="font-medium text-gray-900">AI Generator</div>
                    <div className="text-xs text-gray-500">Generate content with AI</div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveSection('analytics')}
                  className={`w-full flex items-center space-x-3 p-3 text-left border rounded-lg transition-colors ${
                    activeSection === 'analytics'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <TrendingUp className="h-5 w-5 text-yellow-600" />
                  <div>
                    <div className="font-medium text-gray-900">Analytics</div>
                    <div className="text-xs text-gray-500">Blog performance metrics</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Published</span>
                  <span className="font-medium text-green-600">
                    {posts.filter((p) => p.status === 'published').length}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Drafts</span>
                  <span className="font-medium text-yellow-600">
                    {posts.filter((p) => p.status === 'draft').length}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">AI Generated</span>
                  <span className="font-medium text-purple-600">
                    {posts.filter((p) => p.aiGenerated).length}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Views</span>
                  <span className="font-medium text-blue-600">
                    {posts.reduce((sum, p) => sum + p.viewCount, 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto">
          {/* Posts Section */}
          {activeSection === 'posts' && (
            <div className="p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Blog Posts</h3>
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search posts..."
                          className="pl-3 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <select className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                        <option value="all">All Status</option>
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                  </div>
                </div>

                {isLoadingPosts ? (
                  <div className="p-8 text-center">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading posts...</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {posts.map((post) => (
                      <div key={post.id} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-medium text-gray-900">{post.title}</h4>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  post.status === 'published'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {post.status}
                              </span>
                              {post.aiGenerated && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  AI Generated
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 mb-3">{post.excerpt}</p>
                            <div className="flex items-center space-x-6 text-sm text-gray-500">
                              <span>By {post.author?.name || 'Unknown Author'}</span>
                              <span>{formatDate(post.publishedAt)}</span>
                              <span className="flex items-center space-x-1">
                                <Eye className="h-4 w-4" />
                                <span>{post.viewCount} views</span>
                              </span>
                              <span>{post.readingTimeMinutes} min read</span>
                            </div>
                            <div className="flex items-center space-x-2 mt-3">
                              {(post.tags || []).map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                                  style={{
                                    backgroundColor: tag.color + '20',
                                    color: tag.color,
                                  }}
                                >
                                  {tag?.name || 'Unknown Tag'}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleEditPost(post)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit post"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePost(post)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete post"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags Section */}
          {activeSection === 'tags' && (
            <div className="p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Blog Tags</h3>
                    <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                      <Plus className="h-4 w-4" />
                      <span>New Tag</span>
                    </button>
                  </div>
                </div>

                {isLoadingTags ? (
                  <div className="p-8 text-center">
                    <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading tags...</p>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {tags.map((tag) => (
                        <div
                          key={tag.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            <button className="text-gray-400 hover:text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <h4 className="font-medium text-gray-900 mb-1">{tag.name}</h4>
                          <p className="text-sm text-gray-600">{tag.postCount} posts</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Generator Section */}
          {activeSection === 'ai-generator' && (
            <div className="p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">AI Blog Generator</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Generate high-quality blog content using AI
                  </p>
                </div>
                <div className="p-6">
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      AI Generator Coming Soon
                    </h4>
                    <p className="text-gray-600">
                      This feature will allow you to generate blog content using AI.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && (
            <div className="p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Blog Analytics</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Track blog performance and engagement
                  </p>
                </div>
                <div className="p-6">
                  <div className="text-center py-12">
                    <TrendingUp className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      Analytics Coming Soon
                    </h4>
                    <p className="text-gray-600">
                      Detailed analytics and performance metrics will be available here.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Blog Post Editor Modal */}
      <BlogPostEditor
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingPost(null);
        }}
        post={editingPost}
        tags={tags}
        categories={categories}
        onSave={handleSavePost}
      />
    </div>
  );
}
